import React, { useState, useEffect, useRef } from 'react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from '../../ui/sheet';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Alert, AlertDescription } from '../../ui/alert';
import { Link2, XCircle, Trash2, Building } from 'lucide-react';
import { toast } from 'sonner';
import { useUpdateDataEQConnector } from '../../../hooks/useDataEQConnector';
import { handleMutationError } from '../../../utils/errorHandling';

/**
 * Custom hook for managing auto-hiding error messages
 * @param timeout - Duration in milliseconds before error auto-hides (default: 5000)
 * @returns [error, setErrorWithTimeout, setError]
 */
const useAutoHideError = (timeout = 5000) => {
  const [error, setError] = useState('');
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const setErrorWithTimeout = (message: string) => {
    setError(message);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setError(''), timeout);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  return [error, setErrorWithTimeout, setError] as const;
};

// Account with frontend ID for stable React keys
interface AccountWithId {
  id: string; // Frontend-only, for React key stability
  code: string;
  dataeqApiKey: string;
}

interface DataEQConnector {
  id?: string;
  type: 'dataeq';
  name: string; // Base name from database
  status: 'active' | 'inactive' | 'draft' | 'error';
  requiresCredentials: true;
  connectorName?: string; // User-defined display name (preferred if present)
  organisationCode?: string;
  dataeqAccounts?: Array<{ code: string; dataeqApiKey: string }>;
}

interface DataEQConnectorConfigSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  connector?: DataEQConnector | null;
}

// Placeholder for masked API keys from backend (used for change detection)
const MASKED_KEY_PLACEHOLDER = '********';

// DataEQ connector configuration
const DATAEQ_CONNECTOR_CONFIG = {
  name: 'DataEQ',
  requiresCredentials: true,
  credentialFields: ['apiKey', 'orgCode'] as const,
  description: 'Real-time conduct signal feed from DataEQ platform',
  icon: <Link2 className="w-4 h-4" />,
} as const;

const DataEQConnectorConfigSheet: React.FC<DataEQConnectorConfigSheetProps> = ({
  open,
  onOpenChange,
  connector,
}) => {
  const isEdit = !!connector?.id;

  const [formData, setFormData] = useState({
    name: '',
    code: '',
    dataeqAccounts: [{ id: crypto.randomUUID(), code: '', dataeqApiKey: '' }] as AccountWithId[],
    status: 'draft' as 'active' | 'inactive' | 'draft',
  });

  // Track original values for change detection (PATCH semantics)
  const [originalData, setOriginalData] = useState({
    name: '',
    dataeqAccounts: [] as AccountWithId[],
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [generalError, setGeneralErrorWithTimeout, setGeneralError] = useAutoHideError(5000);

  // Use the update connector mutation
  const updateConnector = useUpdateDataEQConnector();

  useEffect(() => {
    if (connector) {
      // Normalize display name: prefer connectorName (user-defined), fall back to name (base)
      const connectorName = connector.connectorName || connector.name;
      const accounts: AccountWithId[] = connector.dataeqAccounts && connector.dataeqAccounts.length > 0 ?
        connector.dataeqAccounts.map(acc => ({
          id: crypto.randomUUID(), // Generate stable ID for React keys
          code: acc.code,
          dataeqApiKey: MASKED_KEY_PLACEHOLDER // Use consistent placeholder for masked keys
        })) :
        [{ id: crypto.randomUUID(), code: '', dataeqApiKey: '' }];

      // Store with placeholder for existing accounts (allows change detection)
      setFormData({
        name: connectorName,
        code: connector.organisationCode || '',
        dataeqAccounts: accounts,
        status: connector.status === 'error' ? 'draft' : connector.status,
      });

      // Store original values for change tracking
      setOriginalData({
        name: connectorName,
        dataeqAccounts: accounts,
      });
    } else {
      setFormData({
        name: '',
        code: '',
        dataeqAccounts: [{ id: crypto.randomUUID(), code: '', dataeqApiKey: '' }],
        status: 'draft',
      });
      setOriginalData({
        name: '',
        dataeqAccounts: [],
      });
    }
    setErrors({});
    setGeneralError('');
  }, [connector, open, setGeneralError]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Connector name is required';
    }

    if (!formData.code.trim()) {
      newErrors.code = 'Connector code is required';
    }

    // DataEQ-specific validation: at least one account must have both code and API key
    const validAccounts = formData.dataeqAccounts.filter(
      acc => acc.code.trim() && acc.dataeqApiKey.trim()
    );

    if (validAccounts.length === 0) {
      newErrors.dataeqAccounts = 'At least one DataEQ account with code and API key is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    // Build PATCH request - only send what has changed
    const patchRequest: {
      connectorName?: string;
      removeDataeqAccounts?: string[];
      addDataeqAccounts?: Array<{ code: string; dataeqApiKey: string }>;
    } = {};

    // 1. Check if connector name changed
    if (formData.name !== originalData.name) {
      patchRequest.connectorName = formData.name;
    }

    // 2. Detect account changes
    const originalAccountCodes = new Set(originalData.dataeqAccounts.map(acc => acc.code));
    const currentAccounts = formData.dataeqAccounts.filter(
      acc => acc.code.trim() && acc.dataeqApiKey.trim()
    );
    const currentAccountsMap = new Map(currentAccounts.map(acc => [acc.code, acc]));

    // Find removed accounts (in original but not in current)
    const removedAccounts = originalData.dataeqAccounts
      .filter(acc => !currentAccountsMap.has(acc.code))
      .map(acc => acc.code);

    if (removedAccounts.length > 0) {
      patchRequest.removeDataeqAccounts = removedAccounts;
    }

    // Find added or modified accounts
    // - New account: not in original
    // - Modified account: API key changed (user entered a new key, not the masked placeholder)
    const addedOrModifiedAccounts = currentAccounts.filter(acc => {
      if (!originalAccountCodes.has(acc.code)) {
        // New account
        return true;
      }
      // Check if API key changed from the placeholder
      const original = originalData.dataeqAccounts.find(orig => orig.code === acc.code);
      // Only include if user changed the API key (not still the placeholder)
      return original && acc.dataeqApiKey !== MASKED_KEY_PLACEHOLDER && acc.dataeqApiKey !== original.dataeqApiKey;
    });

    if (addedOrModifiedAccounts.length > 0) {
      // Strip frontend-only 'id' field before sending to backend
      patchRequest.addDataeqAccounts = addedOrModifiedAccounts.map(({ code, dataeqApiKey }) => ({
        code,
        dataeqApiKey,
      }));
    }

    // Only send if there are actual changes
    if (Object.keys(patchRequest).length === 0) {
      toast.info('No Changes', {
        description: 'No changes were made to the connector.',
      });
      onOpenChange(false);
      return;
    }

    updateConnector.mutate(
      patchRequest,
      {
        onSuccess: () => {
          // Close sheet
          onOpenChange(false);

          // Build change summary
          const changesSummary = [];
          if (patchRequest.connectorName) changesSummary.push('name updated');
          if (patchRequest.addDataeqAccounts) changesSummary.push(`${patchRequest.addDataeqAccounts.length} account(s) added/updated`);
          if (patchRequest.removeDataeqAccounts) changesSummary.push(`${patchRequest.removeDataeqAccounts.length} account(s) removed`);

          // Show success toast
          toast.success('Connector Updated', {
            description: `Successfully updated: ${changesSummary.join(', ')}`,
          });
        },
        onError: (error) => {
          const errorToast = handleMutationError(error, setErrors, {
            notFoundTitle: 'Connector Not Found',
            notFoundDescription: 'The connector you are trying to update does not exist.',
            genericTitle: 'Failed to Update Connector',
            genericDescription: 'An error occurred while updating the connector. Please try again later.',
          });

          // For non-validation errors, show in-form alert with auto-hide
          if (!error.isValidationError()) {
            setGeneralErrorWithTimeout(errorToast.description);
          }
        },
      }
    );
  };

  const handleAddAccount = () => {
    setFormData(prev => ({
      ...prev,
      dataeqAccounts: [...prev.dataeqAccounts, { id: crypto.randomUUID(), code: '', dataeqApiKey: '' }],
    }));
  };

  const handleRemoveAccount = (index: number) => {
    setFormData(prev => ({
      ...prev,
      dataeqAccounts: prev.dataeqAccounts.filter((_, i) => i !== index),
    }));
  };

  const handleAccountChange = (index: number, field: 'code' | 'dataeqApiKey', value: string) => {
    setFormData(prev => ({
      ...prev,
      dataeqAccounts: prev.dataeqAccounts.map((account, i) =>
        i === index ? { ...account, [field]: value } : account
      ),
    }));
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-[540px] overflow-y-auto p-0" onOpenAutoFocus={(e) => e.preventDefault()}>
        <div className="h-full flex flex-col">
          <SheetHeader className="px-6 py-6 border-b">
            <SheetTitle>
              {isEdit ? `Edit ${DATAEQ_CONNECTOR_CONFIG.name} Connector` : 'Add Connector'}
            </SheetTitle>
            <SheetDescription>
              {isEdit ?
                `Configure your ${DATAEQ_CONNECTOR_CONFIG.name} connector settings` :
                'Configure your DataEQ integration'
              }
            </SheetDescription>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto px-6 py-6">
            <div className="space-y-6">
          {/* General Error Alert */}
          {generalError && (
            <Alert variant="destructive">
              <XCircle className="h-4 w-4" />
              <AlertDescription>{generalError}</AlertDescription>
            </Alert>
          )}

          {/* Connector Type */}
          <div className="space-y-2">
            <Label htmlFor="type">Connector Type</Label>
            <div className="flex h-10 w-fit items-center gap-2 rounded-md border border-input bg-muted/50 px-3 py-2 text-sm">
              {DATAEQ_CONNECTOR_CONFIG.icon}
              <span>{DATAEQ_CONNECTOR_CONFIG.name}</span>
            </div>
          </div>

          {/* Organisation Code */}
          <div className="space-y-2">
            <Label htmlFor="code">Organisation Code</Label>
            {isEdit ? (
              <>
                <div className="flex h-10 w-fit items-center gap-2 rounded-md border border-input bg-muted/50 px-3 py-2 text-sm text-muted-foreground">
                  <Building className="w-4 h-4" />
                  <span>{formData.code}</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  Organisation code cannot be changed after creation
                </p>
              </>
            ) : (
              <Input
                id="code"
                value={formData.code}
                onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value }))}
                placeholder="e.g., admiral-insurance"
              />
            )}
            {errors.code && (
              <p className="text-sm text-red-500">{errors.code}</p>
            )}
          </div>

          {/* Connector Name */}
          <div className="space-y-2">
            <Label htmlFor="name">Connector Name</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder={`e.g., ${DATAEQ_CONNECTOR_CONFIG.name} Production`}
              autoFocus={false}
            />
            <p className="text-xs text-muted-foreground">
              {isEdit
                ? 'Updating this will update the name of the associated dataset in your Vault'
                : 'This will be the name of the dataset in your Vault'
              }
            </p>
            {errors.name && (
              <p className="text-sm text-red-500">{errors.name}</p>
            )}
          </div>

          {/* Credentials Section */}
          <div className="space-y-4">
            {/* DataEQ Accounts */}
            <div className="space-y-4">
                  <Label>DataEQ Accounts</Label>
                  {formData.dataeqAccounts.map((account, index) => (
                    <div key={account.id} className="p-4 border rounded-lg space-y-3">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-medium">Account {index + 1}</h4>
                        {formData.dataeqAccounts.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveAccount(index)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor={`org-code-${index}`}>Code</Label>
                          <Input
                            id={`org-code-${index}`}
                            value={account.code}
                            onChange={(e) => handleAccountChange(index, 'code', e.target.value)}
                            placeholder="e.g., ORG001"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor={`api-key-${index}`}>API Key</Label>
                          <Input
                            id={`api-key-${index}`}
                            type="password"
                            value={account.dataeqApiKey}
                            onChange={(e) => handleAccountChange(index, 'dataeqApiKey', e.target.value)}
                            placeholder={isEdit ? 'Enter new key or leave unchanged' : 'Enter API key'}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleAddAccount}
                  >
                    Add DataEQ Account
                  </Button>
                  {errors.dataeqAccounts && (
                    <p className="text-sm text-red-500">{errors.dataeqAccounts}</p>
                  )}
                </div>
            </div>
            </div>
          </div>

          <SheetFooter className="px-6 py-4 border-t mt-auto">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={updateConnector.isPending}
            >
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={updateConnector.isPending}>
              {updateConnector.isPending ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Connector'}
            </Button>
          </SheetFooter>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default DataEQConnectorConfigSheet;